import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import useTutorialCache from "@hooks/useTutorialCache";
import { formatDate } from "@memorang/helpers/date";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import { ExamDatePickerModal } from "@memorang/ui/components/ExamDatePickerModal";
import { useToast } from "@memorang/ui/components/Toast";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Portal } from "react-native-paper";

type Props = {
	showDatePicker: boolean;
	toggleShowDatePicker: () => void;
	handleDateChange?: (date: Date) => void;
};

const SetExamDateContainer = ({
	showDatePicker,
	toggleShowDatePicker,
	handleDateChange,
}: Props) => {
	const { tutorialData, setTutorialData } = useTutorialCache();
	const { showToast } = useToast();
	const { t } = useTranslation();
	const [hideTutorial, setHideTutorial] = useState(false);

	const examDateDisclaimer = useAppStore(
		(state) => state.exam.examDateDisclaimer,
	);

	const {
		currentExamContext: { examDate },
		updateExamDate,
	} = useExamContext();

	if (!showDatePicker) {
		return null;
	}

	const shouldShowTutorial =
		!hideTutorial && !tutorialData?.hasSeenExamDateTutorial;
	if (shouldShowTutorial) {
		const handleDismissTutorial = () => {
			setTutorialData({ hasSeenExamDateTutorial: true });
			toggleShowDatePicker();
		};

		const handleConfirmTutorial = () => {
			setTutorialData({ hasSeenExamDateTutorial: true });
			setHideTutorial(true);
		};

		return (
			<ConfirmDialog
				visible={true}
				showCloseButton={false}
				title={t("examDate.noticeDialog.title")}
				icon="calendar-outline"
				body={examDateDisclaimer ?? t("examDate.noticeDialog.description")}
				dismissCta={t("examDate.noticeDialog.dismissCta")}
				dismissCallback={handleDismissTutorial}
				confirmCta={t("examDate.noticeDialog.confirmCta")}
				confirmCallback={handleConfirmTutorial}
			/>
		);
	}

	return showDatePicker ? (
		<Portal>
			<ExamDatePickerModal
				isVisible
				onCancel={toggleShowDatePicker}
				initialDate={examDate}
				onConfirm={(date) => {
					toggleShowDatePicker();
					updateExamDate(date);
					handleDateChange?.(date);
					showToast({
						message: t("account.examDateUpdated", { date: formatDate(date) }),
						duration: 3000,
					});
				}}
			/>
		</Portal>
	) : null;
};

export default SetExamDateContainer;
